/**
 * @fileoverview Supported chains and factory addresses for EscapeHub Token Creator.
 *
 * Users can import these configs directly instead of hardcoding addresses.
 *
 * @example
 * ```typescript
 * import { SUPPORTED_CHAINS, getChainConfig } from '@escapehub/token-creator';
 *
 * const baseConfig = getChainConfig(8453); // Base
 * console.log(baseConfig.factoryAddress);
 * ```
 */

/**
 * Chain configuration for token deployment.
 */
export interface SupportedChainConfig {
  /** Chain ID */
  chainId: number;
  /** Chain name */
  name: string;
  /** Factory contract address */
  factoryAddress: string;
  /** Whether this is a testnet */
  testnet: boolean;
  /** Multicall3 address (false if not available) */
  multicall3Address: string | false;
  /** Public RPC URL (may be rate limited) */
  rpcUrl?: string;
}

/**
 * Standard Multicall3 address deployed on most EVM chains.
 */
export const STANDARD_MULTICALL3 = '0xcA11bde05977b3631167028862bE2a173976CA11';

/**
 * All supported chains with their factory addresses.
 *
 * Factory addresses are where the LaunchERC20Factory contract is deployed.
 * These are the official EscapeHub deployments.
 */
export const SUPPORTED_CHAINS: SupportedChainConfig[] = [
  // ═══════════════════════════════════════════════════════════════════════════
  // ETHEREUM
  // ═══════════════════════════════════════════════════════════════════════════
  {
    chainId: 1,
    name: 'Ethereum',
    factoryAddress: '0x1d83c38c19bfc3b06ca00cfb23062e906ffcc4cb',
    testnet: false,
    multicall3Address: STANDARD_MULTICALL3,
    rpcUrl: 'https://ethereum-rpc.publicnode.com',
  },
  {
    chainId: 11155111,
    name: 'Sepolia',
    factoryAddress: '0x10ca27bc0c4f9c1ea9169a76270c0c136d160a54',
    testnet: true,
    multicall3Address: STANDARD_MULTICALL3,
    rpcUrl: 'https://ethereum-sepolia-rpc.publicnode.com',
  },
  {
    chainId: 560048,
    name: 'Hoodi',
    factoryAddress: '0x10ca27bc0c4f9c1ea9169a76270c0c136d160a54',
    testnet: true,
    multicall3Address: STANDARD_MULTICALL3,
    rpcUrl: 'https://rpc.hoodi.ethpandaops.io',
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // BNB SMART CHAIN
  // ═══════════════════════════════════════════════════════════════════════════
  {
    chainId: 56,
    name: 'BNB Smart Chain',
    factoryAddress: '0x66466a3081052d6a819a08d1813d2782bc0f8405',
    testnet: false,
    multicall3Address: STANDARD_MULTICALL3,
    rpcUrl: 'https://bsc-rpc.publicnode.com',
  },
  {
    chainId: 97,
    name: 'BSC Testnet',
    factoryAddress: '0x10ca27bc0c4f9c1ea9169a76270c0c136d160a54',
    testnet: true,
    multicall3Address: STANDARD_MULTICALL3,
    rpcUrl: 'https://bsc-testnet-rpc.publicnode.com',
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // POLYGON
  // ═══════════════════════════════════════════════════════════════════════════
  {
    chainId: 137,
    name: 'Polygon',
    factoryAddress: '0x10ca27bc0c4f9c1ea9169a76270c0c136d160a54',
    testnet: false,
    multicall3Address: STANDARD_MULTICALL3,
    rpcUrl: 'https://polygon-bor-rpc.publicnode.com',
  },
  {
    chainId: 80002,
    name: 'Polygon Amoy',
    factoryAddress: '0x10ca27bc0c4f9c1ea9169a76270c0c136d160a54',
    testnet: true,
    multicall3Address: STANDARD_MULTICALL3,
    rpcUrl: 'https://polygon-amoy-bor-rpc.publicnode.com',
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // ARBITRUM
  // ═══════════════════════════════════════════════════════════════════════════
  {
    chainId: 42161,
    name: 'Arbitrum One',
    factoryAddress: '0x10ca27bc0c4f9c1ea9169a76270c0c136d160a54',
    testnet: false,
    multicall3Address: STANDARD_MULTICALL3,
    rpcUrl: 'https://arbitrum-one-rpc.publicnode.com',
  },
  {
    chainId: 421614,
    name: 'Arbitrum Sepolia',
    factoryAddress: '0x10ca27bc0c4f9c1ea9169a76270c0c136d160a54',
    testnet: true,
    multicall3Address: STANDARD_MULTICALL3,
    rpcUrl: 'https://arbitrum-sepolia-rpc.publicnode.com',
  },
  {
    chainId: 42170,
    name: 'Arbitrum Nova',
    factoryAddress: '0x10ca27bc0c4f9c1ea9169a76270c0c136d160a54',
    testnet: false,
    multicall3Address: STANDARD_MULTICALL3,
    rpcUrl: 'https://nova.arbitrum.io/rpc',
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // OPTIMISM
  // ═══════════════════════════════════════════════════════════════════════════
  {
    chainId: 10,
    name: 'Optimism',
    factoryAddress: '0x10ca27bc0c4f9c1ea9169a76270c0c136d160a54',
    testnet: false,
    multicall3Address: STANDARD_MULTICALL3,
    rpcUrl: 'https://optimism-rpc.publicnode.com',
  },
  {
    chainId: 11155420,
    name: 'OP Sepolia',
    factoryAddress: '0x10ca27bc0c4f9c1ea9169a76270c0c136d160a54',
    testnet: true,
    multicall3Address: STANDARD_MULTICALL3,
    rpcUrl: 'https://optimism-sepolia-rpc.publicnode.com',
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // BASE
  // ═══════════════════════════════════════════════════════════════════════════
  {
    chainId: 8453,
    name: 'Base',
    factoryAddress: '0x10ca27bc0c4f9c1ea9169a76270c0c136d160a54',
    testnet: false,
    multicall3Address: STANDARD_MULTICALL3,
    rpcUrl: 'https://base-rpc.publicnode.com',
  },
  {
    chainId: 84532,
    name: 'Base Sepolia',
    factoryAddress: '0x10ca27bc0c4f9c1ea9169a76270c0c136d160a54',
    testnet: true,
    multicall3Address: STANDARD_MULTICALL3,
    rpcUrl: 'https://base-sepolia-rpc.publicnode.com',
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // AVALANCHE
  // ═══════════════════════════════════════════════════════════════════════════
  {
    chainId: 43114,
    name: 'Avalanche',
    factoryAddress: '0x10ca27bc0c4f9c1ea9169a76270c0c136d160a54',
    testnet: false,
    multicall3Address: STANDARD_MULTICALL3,
    rpcUrl: 'https://avalanche-c-chain-rpc.publicnode.com',
  },
  {
    chainId: 43113,
    name: 'Avalanche Fuji',
    factoryAddress: '0x10ca27bc0c4f9c1ea9169a76270c0c136d160a54',
    testnet: true,
    multicall3Address: STANDARD_MULTICALL3,
    rpcUrl: 'https://avalanche-fuji-c-chain-rpc.publicnode.com',
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // LINEA
  // ═══════════════════════════════════════════════════════════════════════════
  {
    chainId: 59144,
    name: 'Linea',
    factoryAddress: '0x10ca27bc0c4f9c1ea9169a76270c0c136d160a54',
    testnet: false,
    multicall3Address: STANDARD_MULTICALL3,
    rpcUrl: 'https://rpc.linea.build',
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // SCROLL
  // ═══════════════════════════════════════════════════════════════════════════
  {
    chainId: 534352,
    name: 'Scroll',
    factoryAddress: '0x10ca27bc0c4f9c1ea9169a76270c0c136d160a54',
    testnet: false,
    multicall3Address: STANDARD_MULTICALL3,
    rpcUrl: 'https://rpc.scroll.io',
  },
  {
    chainId: 534351,
    name: 'Scroll Sepolia',
    factoryAddress: '0x10ca27bc0c4f9c1ea9169a76270c0c136d160a54',
    testnet: true,
    multicall3Address: STANDARD_MULTICALL3,
    rpcUrl: 'https://sepolia-rpc.scroll.io',
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // MANTLE
  // ═══════════════════════════════════════════════════════════════════════════
  {
    chainId: 5000,
    name: 'Mantle',
    factoryAddress: '0x10ca27bc0c4f9c1ea9169a76270c0c136d160a54',
    testnet: false,
    multicall3Address: STANDARD_MULTICALL3,
    rpcUrl: 'https://rpc.mantle.xyz',
  },
  {
    chainId: 5003,
    name: 'Mantle Sepolia',
    factoryAddress: '0x10ca27bc0c4f9c1ea9169a76270c0c136d160a54',
    testnet: true,
    multicall3Address: STANDARD_MULTICALL3,
    rpcUrl: 'https://rpc.sepolia.mantle.xyz',
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // BLAST
  // ═══════════════════════════════════════════════════════════════════════════
  {
    chainId: 81457,
    name: 'Blast',
    factoryAddress: '0x10ca27bc0c4f9c1ea9169a76270c0c136d160a54',
    testnet: false,
    multicall3Address: STANDARD_MULTICALL3,
    rpcUrl: 'https://rpc.blast.io',
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // CELO
  // ═══════════════════════════════════════════════════════════════════════════
  {
    chainId: 42220,
    name: 'Celo',
    factoryAddress: '0x10ca27bc0c4f9c1ea9169a76270c0c136d160a54',
    testnet: false,
    multicall3Address: STANDARD_MULTICALL3,
    rpcUrl: 'https://forno.celo.org',
  },
  {
    chainId: 44787,
    name: 'Celo Alfajores',
    factoryAddress: '0x10ca27bc0c4f9c1ea9169a76270c0c136d160a54',
    testnet: true,
    multicall3Address: STANDARD_MULTICALL3,
    rpcUrl: 'https://alfajores-forno.celo-testnet.org',
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // MOONBEAM
  // ═══════════════════════════════════════════════════════════════════════════
  {
    chainId: 1284,
    name: 'Moonbeam',
    factoryAddress: '0x98642b5ac10167c2794729f196dcaa4db9cefa66',
    testnet: false,
    multicall3Address: STANDARD_MULTICALL3,
    rpcUrl: 'https://rpc.api.moonbeam.network',
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // SONIC
  // ═══════════════════════════════════════════════════════════════════════════
  {
    chainId: 146,
    name: 'Sonic',
    factoryAddress: '0x10ca27bc0c4f9c1ea9169a76270c0c136d160a54',
    testnet: false,
    multicall3Address: STANDARD_MULTICALL3,
    rpcUrl: 'https://rpc.soniclabs.com',
  },
  {
    chainId: 14601,
    name: 'Sonic Testnet',
    factoryAddress: '0x10ca27bc0c4f9c1ea9169a76270c0c136d160a54',
    testnet: true,
    multicall3Address: STANDARD_MULTICALL3,
    rpcUrl: 'https://rpc.testnet.soniclabs.com',
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // PEPE UNCHAINED
  // ═══════════════════════════════════════════════════════════════════════════
  {
    chainId: 97741,
    name: 'Pepe Unchained',
    factoryAddress: '0x10ca27bc0c4f9c1ea9169a76270c0c136d160a54',
    testnet: false,
    multicall3Address: false, // No standard Multicall3
    rpcUrl: 'https://rpc.pepeunchained.com',
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // SWELLCHAIN
  // ═══════════════════════════════════════════════════════════════════════════
  {
    chainId: 1923,
    name: 'Swell',
    factoryAddress: '0x10ca27bc0c4f9c1ea9169a76270c0c136d160a54',
    testnet: false,
    multicall3Address: STANDARD_MULTICALL3,
    rpcUrl: 'https://swell-mainnet.alt.technology',
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // MONAD
  // ═══════════════════════════════════════════════════════════════════════════
  {
    chainId: 143,
    name: 'Monad',
    factoryAddress: '0x10ca27bc0c4f9c1ea9169a76270c0c136d160a54',
    testnet: false,
    multicall3Address: STANDARD_MULTICALL3,
    rpcUrl: 'https://rpc.monad.xyz',
  },
  {
    chainId: 10143,
    name: 'Monad Testnet',
    factoryAddress: '0x10ca27bc0c4f9c1ea9169a76270c0c136d160a54',
    testnet: true,
    multicall3Address: STANDARD_MULTICALL3,
    rpcUrl: 'https://testnet-rpc.monad.xyz',
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // SEI
  // ═══════════════════════════════════════════════════════════════════════════
  {
    chainId: 1329,
    name: 'Sei',
    factoryAddress: '0x98642b5ac10167c2794729f196dcaa4db9cefa66',
    testnet: false,
    multicall3Address: STANDARD_MULTICALL3,
    rpcUrl: 'https://evm-rpc.sei-apis.com',
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // GNOSIS
  // ═══════════════════════════════════════════════════════════════════════════
  {
    chainId: 100,
    name: 'Gnosis',
    factoryAddress: '0x10ca27bc0c4f9c1ea9169a76270c0c136d160a54',
    testnet: false,
    multicall3Address: STANDARD_MULTICALL3,
    rpcUrl: 'https://rpc.gnosischain.com',
  },
  {
    chainId: 10200,
    name: 'Gnosis Chiado',
    factoryAddress: '0x98642b5ac10167c2794729f196dcaa4db9cefa66',
    testnet: true,
    multicall3Address: STANDARD_MULTICALL3,
    rpcUrl: 'https://rpc.chiadochain.net',
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // FLARE
  // ═══════════════════════════════════════════════════════════════════════════
  {
    chainId: 14,
    name: 'Flare',
    factoryAddress: '0x10ca27bc0c4f9c1ea9169a76270c0c136d160a54',
    testnet: false,
    multicall3Address: STANDARD_MULTICALL3,
    rpcUrl: 'https://flare-api.flare.network/ext/C/rpc',
  },
  {
    chainId: 114,
    name: 'Flare Coston2',
    factoryAddress: '0x10ca27bc0c4f9c1ea9169a76270c0c136d160a54',
    testnet: true,
    multicall3Address: STANDARD_MULTICALL3,
    rpcUrl: 'https://coston2-api.flare.network/ext/C/rpc',
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // X LAYER
  // ═══════════════════════════════════════════════════════════════════════════
  {
    chainId: 196,
    name: 'X Layer',
    factoryAddress: '0x10ca27bc0c4f9c1ea9169a76270c0c136d160a54',
    testnet: false,
    multicall3Address: STANDARD_MULTICALL3,
    rpcUrl: 'https://rpc.xlayer.tech',
  },
  {
    chainId: 1952,
    name: 'X Layer Testnet',
    factoryAddress: '0x10ca27bc0c4f9c1ea9169a76270c0c136d160a54',
    testnet: true,
    multicall3Address: STANDARD_MULTICALL3,
    rpcUrl: 'https://testrpc.xlayer.tech',
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // KAIA
  // ═══════════════════════════════════════════════════════════════════════════
  {
    chainId: 8217,
    name: 'Kaia',
    factoryAddress: '0x98642b5ac10167c2794729f196dcaa4db9cefa66',
    testnet: false,
    multicall3Address: STANDARD_MULTICALL3,
    rpcUrl: 'https://public-en.node.kaia.io',
  },
  {
    chainId: 1001,
    name: 'Kaia Kairos',
    factoryAddress: '0x10ca27bc0c4f9c1ea9169a76270c0c136d160a54',
    testnet: true,
    multicall3Address: STANDARD_MULTICALL3,
    rpcUrl: 'https://public-en-kairos.node.kaia.io',
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // UNICHAIN
  // ═══════════════════════════════════════════════════════════════════════════
  {
    chainId: 130,
    name: 'Unichain',
    factoryAddress: '0x98642b5ac10167c2794729f196dcaa4db9cefa66',
    testnet: false,
    multicall3Address: STANDARD_MULTICALL3,
    rpcUrl: 'https://mainnet.unichain.org',
  },
];

/**
 * Map of chain ID to chain config for quick lookup.
 */
export const CHAIN_MAP: Map<number, SupportedChainConfig> = new Map(
  SUPPORTED_CHAINS.map(chain => [chain.chainId, chain])
);

/**
 * Get chain configuration by chain ID.
 *
 * @param chainId - The chain ID to look up
 * @returns Chain config or undefined if not supported
 *
 * @example
 * ```typescript
 * import { getChainConfig } from '@escapehub/token-creator';
 *
 * const base = getChainConfig(8453);
 * if (base) {
 *   console.log(base.factoryAddress); // '0x10ca27bc0c4f9c1ea9169a76270c0c136d160a54'
 * }
 * ```
 */
export function getChainConfig(chainId: number): SupportedChainConfig | undefined {
  return CHAIN_MAP.get(chainId);
}

/**
 * Get factory address for a chain.
 *
 * @param chainId - The chain ID
 * @returns Factory address or undefined if not supported
 *
 * @example
 * ```typescript
 * import { getFactoryAddress } from '@escapehub/token-creator';
 *
 * const factory = getFactoryAddress(8453); // Base
 * // '0x10ca27bc0c4f9c1ea9169a76270c0c136d160a54'
 * ```
 */
export function getFactoryAddress(chainId: number): string | undefined {
  return CHAIN_MAP.get(chainId)?.factoryAddress;
}

/**
 * Check if a chain is supported.
 *
 * @param chainId - The chain ID to check
 * @returns true if the chain is supported
 */
export function isChainSupported(chainId: number): boolean {
  return CHAIN_MAP.has(chainId);
}

/**
 * Get all mainnet chains.
 */
export function getMainnets(): SupportedChainConfig[] {
  return SUPPORTED_CHAINS.filter(chain => !chain.testnet);
}

/**
 * Get all testnet chains.
 */
export function getTestnets(): SupportedChainConfig[] {
  return SUPPORTED_CHAINS.filter(chain => chain.testnet);
}
